import { HUImageDtoSchema } from "@/modules/dtos/imageDto"
import { UserDtoSchema } from "@/modules/profile/userProfile/userDto"
import { BoxStaffItemDtoSchema } from "@modules/boxStaff/gateway/boxStaffDto"
import { z } from "zod"

// Daily Schedule
export const DailyScheduleDtoSchema = z
  .object({
    slotId: z.string(),
    slotStart: z.string().nullable(),
    slotEnd: z.string().nullable(),
    programName: z.string().nullable(),
    color: z.string().nullable(),
    assignedStaffMembers: UserDtoSchema.nullable(),
    numberOfTrials: z.number().nullable(),
    numberOfDropIns: z.number().nullable(),
    numberOfParticipants: z.number().nullable(),
    numberOfActualParticipants: z.number().nullable(),
    numberOfPendingParticipants: z.number().nullable(),
  })
  .meta({ id: "DailyScheduleDtoSchema" })

export type DailyScheduleDto = z.infer<typeof DailyScheduleDtoSchema>

export const DailyScheduleListSchema = z.array(DailyScheduleDtoSchema).meta({ id: "DailyScheduleListSchema" })

export type DailyScheduleListDto = z.infer<typeof DailyScheduleListSchema>

const SlotParticipantLightDtoSchema = z.object({
  boxMemberId: z.string(),
  username: z.string(),
  firstname: z.string().nullable(),
  lastname: z.string().nullable(),
  slotStart: z.string().nullable(),
  programName: z.string().nullable(),
  assignedStaffMembers: UserDtoSchema.nullable(),
})

const BirthdayMembersDtoSchema = z.object({
  boxMemberId: z.string(),
  username: z.string(),
  firstname: z.string().nullable(),
  lastname: z.string().nullable(),
  slotStart: z.string().nullable(),
  programName: z.string().nullable(),
  assignedStaffMembers: UserDtoSchema.nullable(),
  lastSessionDate: z.string().nullable(),
  nextSessionDate: z.string().nullable(),
})

const TrialsDropInsBirthdaysDtoSchema = z.object({
  dayDate: z.string(), // ISO date string (LocalDate)
  numberOfTrials: z.number().nullable(),
  numberOfDropIns: z.number().nullable(),
  numberOfBirthdays: z.number().nullable(),
  trials: z.array(SlotParticipantLightDtoSchema).nullable(),
  dropIns: z.array(SlotParticipantLightDtoSchema).nullable(),
  birthdays: z.array(BirthdayMembersDtoSchema).nullable(),
})

export const WeeklyTrialsDropInsBirthdaysDtoSchema = z
  .array(TrialsDropInsBirthdaysDtoSchema)
  .meta({ id: "WeeklyTrialsDropInsBirthdaysDtoSchema" })

export type SlotParticipantLightDto = z.infer<typeof SlotParticipantLightDtoSchema>
export type BirthdayMembersDto = z.infer<typeof BirthdayMembersDtoSchema>
export type TrialsDropInsBirthdaysDto = z.infer<typeof TrialsDropInsBirthdaysDtoSchema>
export type WeeklyTrialsDropInsBirthdaysDto = z.infer<typeof WeeklyTrialsDropInsBirthdaysDtoSchema>

// Team Tasks
export const TaskStatusSchema = z.enum(["CREATED", "COMPLETED", "DELETED"]).meta({ id: "TaskStatusSchema" })
export type TaskStatus = z.infer<typeof TaskStatusSchema>

export const TeamTaskDtoSchema = z
  .object({
    id: z.string(),
    task: z.string(),
    taskStatus: TaskStatusSchema,
    creator: BoxStaffItemDtoSchema,
    updater: BoxStaffItemDtoSchema.nullable(),
    createdDate: z.string(),
    updatedDate: z.string().nullable(),
    completedDate: z.string().nullable(),
    archivedDate: z.string().nullable(),
    archived: z.boolean(),
  })
  .meta({ id: "TeamTaskDtoSchema" })

export type TeamTaskDto = z.infer<typeof TeamTaskDtoSchema>

export const TeamTasksListDtoSchema = z.array(TeamTaskDtoSchema).meta({ id: "TeamTasksListDtoSchema" })

export const UpsertTeamTaskDtoSchema = z
  .object({
    task: z.string().min(3).max(240).optional(),
    taskStatus: TaskStatusSchema.optional(),
  })
  .meta({ id: "UpsertTeamTaskDtoSchema" })

export type UpsertTeamTaskDto = z.infer<typeof UpsertTeamTaskDtoSchema>

export const BoxMemberDtoSchema = z
  .object({
    id: z.string(),
    firstname: z.string().nullable(),
    lastname: z.string().nullable(),
    status: z.string(),
    avatar: HUImageDtoSchema.nullable(),
    username: z.string().nullable(),
    note: z.string().nullable(),
    lastVisited: z.string().nullable(),
    noShowPastMonth: z.number().nullable(),
    activeOffers: z.array(
      z.object({
        offerName: z.string(),
        endValidity: z.string().nullable(),
        purchaseEndDate: z.string().nullable(),
        endSubscription: z.string().nullable(),
        subscriptionStatus: z.string().nullable(),
      }),
    ),
    archivedDate: z.string().nullable(),
    inscriptionDate: z.string().nullable(),
    archived: z.boolean().nullable(),
    lastMembership: z.string().nullable(),
  })
  .meta({ id: "BoxMemberDtoSchema" })

export const InactiveMemberLastSlotDtoSchema = z
  .object({
    id: z.string(),
    startTime: z.string(),
    endTime: z.string(),
    programId: z.string(),
    registeredParticipantCount: z.number(),
    pendingParticipantCount: z.number(),
    participantLimit: z.number().nullable(),
    sessionId: z.string().nullable(),
    location: z.string().nullable(),
    isFromTemplate: z.boolean(),
    participation: z
      .object({
        limitStart: z.number(),
        limitEnd: z.number(),
        limitCancel: z.number(),
      })
      .nullable(),
    allowMultiBooking: z.boolean().nullable(),
    dropinsAccepted: z.boolean(),
    trialsAccepted: z.boolean(),
    assignedStaffMembers: z.array(BoxStaffItemDtoSchema),
  })
  .meta({ id: "InactiveMemberLastSlotDtoSchema" })

export const InactiveMemberDtoSchema = z
  .object({
    boxMemberItemDto: BoxMemberDtoSchema,
    numberOfDays: z.number(),
    purchaseName: z.string(),
    lastSlot: InactiveMemberLastSlotDtoSchema.nullable(),
    lastDate: z.string(),
  })
  .meta({ id: "InactiveMemberDtoSchema" })

export type BoxMemberDto = z.infer<typeof BoxMemberDtoSchema>
export type InactiveMemberLastSlotDto = z.infer<typeof InactiveMemberLastSlotDtoSchema>
export type InactiveMemberDto = z.infer<typeof InactiveMemberDtoSchema>

export const InactiveMembersListDtoSchema = z
  .array(InactiveMemberDtoSchema)
  .meta({ id: "InactiveMembersListDtoSchema" })

// Rookie Members
export const RookieMilestoneSchema = z
  .enum(["UNDER_15_DAYS", "UNDER_30_DAYS", "UNDER_45_DAYS", "UNDER_60_DAYS"])
  .meta({ id: "RookieMilestoneSchema" })
export type RookieMilestone = z.infer<typeof RookieMilestoneSchema>

export const DashboardRookieMemberDtoSchema = z
  .object({
    id: z.string(),
    totalSessions: z.number(),
    nbOfDaysSinceRegistration: z.number(),
    lastSessionDate: z.string().nullable(),
    nextSessionDate: z.string().nullable(),
    member: BoxMemberDtoSchema,
    purchaseName: z.string(),
    archived: z.boolean(),
    archivedDate: z.string().nullable(),
    createdDate: z.string(),
  })
  .meta({ id: "DashboardRookieMemberDtoSchema" })

export type DashboardRookieMemberDto = z.infer<typeof DashboardRookieMemberDtoSchema>

export const DashboardRookieMembersListDtoSchema = z
  .array(DashboardRookieMemberDtoSchema)
  .meta({ id: "DashboardRookieMembersListDtoSchema" })

export const TotalRookiesDtoSchema = z
  .object({
    total: z.number(),
  })
  .meta({ id: "TotalRookiesDtoSchema" })

export type TotalRookiesDto = z.infer<typeof TotalRookiesDtoSchema>

// Final Round Members
export const FinalRoundTypeSchema = z.enum(["CLASS_PASS", "MEMBERSHIP"]).meta({ id: "FinalRoundTypeSchema" })
export type FinalRoundType = z.infer<typeof FinalRoundTypeSchema>

export const DashboardFinalRoundDtoSchema = z
  .object({
    id: z.string(),
    boxMember: BoxMemberDtoSchema,
    purchaseName: z.string(),
    remainingCount: z.number().nullable(),
    totalCount: z.number().nullable(),
    endDate: z.string().nullable(),
    sessionsLast30Days: z.number(),
    type: FinalRoundTypeSchema,
  })
  .meta({ id: "DashboardFinalRoundDtoSchema" })

export type DashboardFinalRoundDto = z.infer<typeof DashboardFinalRoundDtoSchema>

export const DashboardFinalRoundListDtoSchema = z
  .array(DashboardFinalRoundDtoSchema)
  .meta({ id: "DashboardFinalRoundListDtoSchema" })

export const TotalFinalRoundDtoSchema = z
  .object({
    total: z.number(),
    classPassTotal: z.number(),
    membershipTotal: z.number(),
  })
  .meta({ id: "TotalFinalRoundDtoSchema" })

export type TotalFinalRoundDto = z.infer<typeof TotalFinalRoundDtoSchema>

// Most Engaged (Commited Club)
export const CommitedClubDtoSchema = z
  .object({
    boxMember: BoxMemberDtoSchema.pick({
      id: true,
      firstname: true,
      lastname: true,
      avatar: true,
    }),
    participationCount: z.number(),
  })
  .meta({ id: "CommitedClubDtoSchema" })

export type CommitedClubDto = z.infer<typeof CommitedClubDtoSchema>
export const CommitedClubListDtoSchema = z.array(CommitedClubDtoSchema).meta({ id: "CommitedClubListDtoSchema" })

// KPI
export const KpiAmountDtoSchema = z
  .object({
    baseline: z.number(),
    comparison: z.number().nullable(),
  })
  .meta({ id: "KpiAmountDtoSchema" })

export type KpiAmountDto = z.infer<typeof KpiAmountDtoSchema>

export const BoxDashboardKpiDtoSchema = z
  .object({
    income: z.object({ total: KpiAmountDtoSchema, mrr: KpiAmountDtoSchema, shopSales: KpiAmountDtoSchema }).nullable(),
    offers: z.object({ membership: KpiAmountDtoSchema, classPass: KpiAmountDtoSchema, none: KpiAmountDtoSchema }),
    activations: z.object({
      membership: KpiAmountDtoSchema,
      classPass: KpiAmountDtoSchema,
      dropIn: KpiAmountDtoSchema,
    }),
    trials: z.object({ count: KpiAmountDtoSchema, conversionRate: KpiAmountDtoSchema }),
  })
  .meta({ id: "BoxDashboardKpiDtoSchema" })

export type BoxDashboardKpiDto = z.infer<typeof BoxDashboardKpiDtoSchema>
